import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { sub, format } from 'date-fns';

import { rejectValue, flatOrder } from 'redux/helpers';
import { showSuccessMessage } from '../notification';
import { getTerminalShippingMethods, selectStore } from '../javelinStore';
import { cleanCart } from '../cart';

import {
  ORDER,
  ORDER_ALL,
  ORDER_ACCEPT,
  ORDER_REJECT,
  ORDER_COMPLETE,
  ORDER_SHIPPED,
  ORDER_DETAIL,
  STRIPE_ORDER_DETAIL,
  SEND_RECEIPT,
  EXPORT_ORDERS,
  ORDER_RECENT,
  ORDER_TOP,
} from './types';
import {
  GET_ORDER_LIST,
  GET_ORDER,
  GET_STRIPE_ORDER,
  POST_ACCEPT_ORDER,
  POST_REJECT_ORDER,
  POST_COMPLETE_ORDER,
  POST_SHIPPED_ORDER,
  POST_SEND_RECEIPT,
  POST_EXPORT_ORDERS,
} from 'services/api';
import { ORDER_STATUS } from 'utils/transform';

const orderAdapter = createEntityAdapter({
  sortComparer: (a, b) => b.updated_at.localeCompare(a.updated_at),
});

export const initialState = orderAdapter.getInitialState({
  loading: false,
  pagination: {
    totalPages: 1,
    currentPage: 1,
  },
  date_filter: { value: 90, label: 'Last 90 Days' },
  status_filter: null,
  search_term: '',
  recent: [],
  top: {},
  page: 1,
  stripeOrder: null,
});

export const getOrderList = createAsyncThunk(
  ORDER_ALL,
  async (_, { rejectWithValue, getState, dispatch }) => {
    const storeId = getState().javelinStore.current;
    const date_filter = getState().order.date_filter?.value;
    const term = getState().order.search_term;
    const status = getState().order.status_filter?.value;
    const page = getState().order.page;

    const from = format(
      sub(new Date(), {
        days: date_filter,
      }),
      'yyyy-MM-dd'
    );

    try {
      const { data, meta } = await GET_ORDER_LIST({ page, from, status, term });

      if (!data.length)
        return { data: [], pagination: meta.pagination, storeId };

      const orderData = data.map((item) => ({
        ...item,
        ...flatOrder(item),
        page: meta.pagination.current_page || 1,
      }));
      return { data: orderData, pagination: meta.pagination, storeId };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
export const getOrder = createAsyncThunk(
  ORDER_DETAIL,
  async ({ id, token }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await GET_ORDER(id, token);
      const _data = {
        ...data,
        ...flatOrder(data),
        product_name: data.product_info?.name || '-',
      };
      if (token && data.store_id) {
        dispatch(getTerminalShippingMethods({storeId: data.store_id}));
        dispatch(cleanCart(data.store_id));
      }
      return _data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getStripeOrder = createAsyncThunk(
  STRIPE_ORDER_DETAIL,
  async ({ id, token }, { rejectWithValue, dispatch }) => {
    try {
      const data = await GET_STRIPE_ORDER(id, token);

      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const acceptOrder = createAsyncThunk(
  ORDER_ACCEPT,
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await POST_ACCEPT_ORDER(id);
      dispatch(showSuccessMessage({ message: 'Order accepted!' }));
      return {
        id: data.id,
        changes: {
          status: ORDER_STATUS[data.status] || data.status.toUpperCase(),
        },
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const rejectOrder = createAsyncThunk(
  ORDER_REJECT,
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await POST_REJECT_ORDER(id);
      dispatch(showSuccessMessage({ message: 'Order rejected!' }));
      return {
        id: data.id,
        changes: {
          status: ORDER_STATUS[data.status] || data.status.toUpperCase(),
        },
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const completeOrder = createAsyncThunk(
  ORDER_COMPLETE,
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await POST_COMPLETE_ORDER(id);
      dispatch(showSuccessMessage({ message: 'Order marked as delivered!' }));
      return {
        id: data.id,
        changes: {
          status: ORDER_STATUS[data.status] || data.status.toUpperCase(),
        },
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const shipOrder = createAsyncThunk(
  ORDER_SHIPPED,
  async ({ id, tracking_id }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await POST_SHIPPED_ORDER(id, tracking_id);
      dispatch(showSuccessMessage({ message: 'Order marked as shipped!' }));
      return {
        id: data.id,
        changes: {
          status: ORDER_STATUS[data.status] || data.status.toUpperCase(),
        },
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const sendReceipt = createAsyncThunk(
  SEND_RECEIPT,
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await POST_SEND_RECEIPT(id);
      dispatch(showSuccessMessage({ message: 'Receipt sent!' }));
      return {
        id: data.id,
        changes: {
          status: ORDER_STATUS[data.status] || data.status.toUpperCase(),
        },
      };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const exportOrders = createAsyncThunk(
  EXPORT_ORDERS,
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const email = getState().auth.user.email;
      const date_filter = getState().order.date_filter?.value;
      const status = getState().order.status_filter?.value;
      const from = format(
        sub(new Date(), {
          days: date_filter,
        }),
        'yyyy-MM-dd'
      );

      await POST_EXPORT_ORDERS({ from, status });
      dispatch(
        showSuccessMessage(
          `Check your email account ${email} for access to the file.`
        )
      );
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getRecentOrders = createAsyncThunk(
  ORDER_RECENT,
  async (_, { rejectWithValue, getState }) => {
    try {
      const { data } = await GET_ORDER_LIST({ per_page: 3 });

      if (!data.length) return [];

      const storeName =
        getState().javelinStore.entities[getState().javelinStore.current].name;

      const orderData = data.map((item) => ({
        ...item,
        ...flatOrder(item),
        storeName,
      }));

      return orderData;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getTopOrder = createAsyncThunk(
  ORDER_TOP,
  async (_, { rejectWithValue, getState }) => {
    try {
      const { data } = await GET_ORDER_LIST({ per_page: 1, order_by: 'total' });

      if (!data.length) return {};

      const storeName =
        getState().javelinStore.entities[getState().javelinStore.current].name;

      const orderData = {
        ...data[0],
        ...flatOrder(data[0]),
        storeName,
      };

      return orderData;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

const orderSlice = createSlice({
  name: ORDER,
  initialState,
  reducers: {
    setDateFilter: (state, { payload }) => {
      state.date_filter = payload;
      if (!!state.status_filter) {
        state.page = 1;
      }
    },
    setStatusFilter: (state, { payload }) => {
      state.status_filter = payload;
      state.page = 1;
    },
    setSearchTerm: (state, { payload }) => {
      state.search_term = payload;
      state.page = 1;
    },
    setOrderPage: (state, { payload }) => {
      state.page = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrderList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOrderList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getOrderList.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (!!payload.storeId) {
          state.pagination = {
            totalPages: payload.pagination.total_pages || 1,
            currentPage: payload.pagination.current_page || 1,
          };
        }
        orderAdapter.setAll(state, payload.data);
      })
      .addCase(getRecentOrders.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.recent = payload;
      })
      .addCase(getTopOrder.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.top = payload;
      })
      .addCase(getRecentOrders.pending, (state, { payload }) => {
        state.loading = true;
      })
      .addCase(getRecentOrders.rejected, (state, { payload }) => {
        state.loading = true;
      })
      .addCase(getTopOrder.pending, (state, { payload }) => {
        state.loading = true;
      })
      .addCase(getTopOrder.rejected, (state, { payload }) => {
        state.loading = true;
      })
      .addCase(getStripeOrder.pending, (state, { payload }) => {
        state.loading = true;
      })
      .addCase(getStripeOrder.rejected, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(getStripeOrder.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.stripeOrder = payload || null;
      })

      .addCase(acceptOrder.fulfilled, orderAdapter.updateOne)
      .addCase(rejectOrder.fulfilled, orderAdapter.updateOne)
      .addCase(completeOrder.fulfilled, orderAdapter.updateOne)
      .addCase(sendReceipt.fulfilled, orderAdapter.updateOne)
      .addCase(shipOrder.fulfilled, orderAdapter.updateOne)
      .addCase(getOrder.fulfilled, orderAdapter.upsertOne)
      .addCase(selectStore.fulfilled, (state) => {
        state.date_filter = initialState.date_filter;
        state.status_filter = initialState.status_filter;
        state.search_term = initialState.search_term;
        state.page = initialState.page;
      })
      .addDefaultCase((state) => state);
  },
});

export const orderSelectors = orderAdapter.getSelectors((state) => state.order);

export const selectOrderById = (state, id) =>
  orderSelectors.selectById(state, id);

export const { setDateFilter, setStatusFilter, setSearchTerm, setOrderPage } =
  orderSlice.actions;
export default orderSlice.reducer;
